import { FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { ArrayElement } from 'dibs-ts-utils/exports/ArrayElement';
import { ExpandingAreaWithReadMore } from 'dibs-elements/exports/ExpandingArea';
import { FormattedMessage } from 'dibs-react-intl';

// components
import { PdpRespDetail } from './PdpRespDetail';
import { PdpSharedDetail, PdpSharedDetailProps } from '../shared/PdpSharedDetail/PdpSharedDetail';
import { PdpSharedDetailCondition } from '../PdpSharedDetails/PdpSharedDetailCondition';
import { PdpSharedDetailMovement } from '../shared/PdpSharedDetail/PdpSharedDetailMovement';
import { PdpSharedDetailFramingOptions } from '../PdpSharedDetails/PdpSharedDetailFramingOptions';
import { PdpSharedRingSizeDetail } from '../PdpSharedRingSize/PdpSharedRingSizeDetail';
import { PdpSharedDetailReference } from '../shared/PdpSharedDetail/PdpSharedDetailReference';
import { PdpSharedDetailLabReport } from '../PdpSharedDetails/PdpSharedDetailLabReport';

import { detailsTitle } from '../messages/pdpDetailsMessages';

// helpers
import slugify from 'dibs-slugify/exports/slugify';

// styles
import styles from './PdpRespDetails.scss';

import {
    PdpRespDetails_item$key,
    PdpRespDetails_item$data,
} from './__generated__/PdpRespDetails_item.graphql';
import { PdpRespDetails_user$key } from './__generated__/PdpRespDetails_user.graphql';

const detailsToHideInPrint = ['PRODUCTION_TIME'];

type Item = NonNullable<PdpRespDetails_item$data>;
type PdpDetailsDisplay = NonNullable<ArrayElement<Item['pdpDetailsDisplay']>>;

type RenderDetailsProps = {
    detailKey: PdpDetailsDisplay['detailKey'];
    delimiter: PdpDetailsDisplay['delimiter'];
    detailValues: PdpSharedDetailProps['detailValues'];
};

type Props = {
    item: PdpRespDetails_item$key;
    user: PdpRespDetails_user$key;
    isClient: boolean;
    currency: string;
    selectedSkuId?: string | null;
    showHeader?: boolean;
    variant?: 'pdpRedesignVariant' | 'pdpRespTabsVariant';
    collapsedHeight?: number;
};

const detailsToIgnore = ['FRAMING_STATUS'];

const notDisplayableDetails = (detail: PdpDetailsDisplay): boolean =>
    !detailsToIgnore.includes(detail?.detailKey || '');

export const PdpRespDetails: FC<Props> = ({
    item: itemRef,
    user: userRef,
    isClient,
    currency,
    selectedSkuId,
    showHeader = true,
    variant,
    collapsedHeight,
}) => {
    const inlineLabels = variant === 'pdpRedesignVariant' || variant === 'pdpRespTabsVariant';

    const item = useFragment(
        graphql`
            fragment PdpRespDetails_item on Item {
                serviceId
                pdpDetailsDisplay {
                    detailKey
                    delimiter
                    detailDisplayName
                    detailValues {
                        __typename
                        ...PdpSharedDetail_detailValues
                        ...PdpSharedRingSizeDetail_detailValues
                        ...PdpSharedDetailMovement_detailValues
                    }
                }
                ...PdpSharedDetailFramingOptions_item
                ...PdpSharedDetailReference_item
            }
        `,
        itemRef
    );
    const user = useFragment(
        graphql`
            fragment PdpRespDetails_user on User {
                ...PdpSharedDetailFramingOptions_user
                ...PdpSharedDetailReference_user
            }
        `,
        userRef
    );

    const details = item?.pdpDetailsDisplay || [];

    const RenderDetails: FC<RenderDetailsProps> = ({ detailKey, detailValues, delimiter }) => {
        const sharedProps: PdpSharedDetailProps = {
            currency,
            detailValues,
            selectedSkuId,
            delimiter,
        };

        if (detailKey === 'REFERENCE_NUMBER') {
            return (
                <PdpSharedDetailReference
                    {...sharedProps}
                    displayValuesAs="block"
                    item={item}
                    user={user}
                />
            );
        }

        // Reference number should always display detail values as blocks, but rest should be inline based on prop
        if (inlineLabels) {
            sharedProps.displayValuesAs = 'inline-block';
        }

        if (detailKey === 'FRAMING') {
            const frameIncludedDetails = details?.find(
                detail => detail?.detailKey === 'FRAMING_STATUS'
            );
            return (
                <>
                    {!!frameIncludedDetails && <div>{frameIncludedDetails.detailDisplayName}</div>}
                    <PdpSharedDetailFramingOptions {...sharedProps} item={item} user={user} />
                </>
            );
        }

        if (detailKey === 'CONDITION' && item?.serviceId) {
            return <PdpSharedDetailCondition {...sharedProps} itemId={item.serviceId} />;
        }

        if (detailKey === 'MOVEMENT') {
            return <PdpSharedDetailMovement {...sharedProps} />;
        }

        if (detailKey === 'RING_SIZE') {
            // the Ring Size component has a different __typename=PdpDetailRingSize vs PdpDetailValue
            // @ts-ignore too long to leave comment about the type error
            return <PdpSharedRingSizeDetail isClient={isClient} detailValues={detailValues} />;
        }

        if (detailKey === 'LAB_REPORT') {
            return <PdpSharedDetailLabReport {...sharedProps} isClient={isClient} />;
        }

        return <PdpSharedDetail {...sharedProps} />;
    };

    const detailMap = details
        .filter(filterNulls)
        .filter(notDisplayableDetails)
        .map((detail, index) => {
            const { detailKey, detailDisplayName, delimiter, detailValues } = detail;
            const shouldHideInPrint = !!(detailKey && detailsToHideInPrint.includes(detailKey));

            return (
                <PdpRespDetail
                    inlineLabel={inlineLabels}
                    delimiter={delimiter}
                    title={detailDisplayName}
                    dataTn={slugify(detailDisplayName)}
                    key={`${detailKey}-${index}`}
                    printHide={shouldHideInPrint}
                >
                    <RenderDetails
                        detailKey={detailKey}
                        // @ts-ignore too long to leave comment about the type error
                        detailValues={detailValues}
                        delimiter={delimiter}
                    />
                </PdpRespDetail>
            );
        });

    return (
        <div
            className={classnames(styles.wrapper, {
                [styles.pdpRedesignVariant]: variant === 'pdpRedesignVariant',
                [styles.pdpRespTabsVariant]: variant === 'pdpRespTabsVariant',
            })}
            data-tn="pdp-details"
        >
            {showHeader && (
                <HeadingLevel>
                    {Heading => <Heading className={styles.header}>{detailsTitle}</Heading>}
                </HeadingLevel>
            )}
            <ul className={styles.detailsWrapper}>
                {variant === 'pdpRespTabsVariant' ? (
                    <ExpandingAreaWithReadMore
                        collapsedHeight={collapsedHeight || 192}
                        showGradient={false}
                        readMore={
                            <FormattedMessage
                                id="PdpRespDetails.viewMore"
                                defaultMessage="View More"
                            />
                        }
                        readLess={
                            <FormattedMessage
                                id="PdpRespDetails.viewLesse"
                                defaultMessage="View Less"
                            />
                        }
                    >
                        {detailMap}
                    </ExpandingAreaWithReadMore>
                ) : (
                    detailMap
                )}
            </ul>
        </div>
    );
};
